import { supabase } from '@/lib/supabase';

// Uso versus limite do plano da conta.
//
// A fonte da verdade é o banco: o bloqueio real acontece em gatilho
// (supabase/migrations/0003_cobranca_limites.sql). O que existe aqui é
// experiência de usuário — mostrar o quanto resta e explicar o bloqueio quando
// ele acontece.

export interface UsoRecurso {
  uso: number;
  limite: number;
  disponivel: number;
}

export interface UsoDoPlano {
  plano: { id: string; nome: string; preco: number } | null;
  condominios: UsoRecurso;
  unidades: UsoRecurso;
  usuarios: UsoRecurso;
}

export type RecursoLimitado = 'condominios' | 'unidades' | 'usuarios';

const RECURSO_LABEL: Record<RecursoLimitado, string> = {
  condominios: 'condomínios',
  unidades: 'unidades',
  usuarios: 'usuários',
};

/** Limite 0 significa "sem teto" — usado por planos legados sem configuração. */
export function ilimitado(recurso: UsoRecurso): boolean {
  return recurso.limite === 0;
}

export function atingiuLimite(recurso: UsoRecurso): boolean {
  return !ilimitado(recurso) && recurso.uso >= recurso.limite;
}

export function podeAdicionar(recurso: UsoRecurso, quantidade = 1): boolean {
  return ilimitado(recurso) || recurso.uso + quantidade <= recurso.limite;
}

export function descreverUso(recurso: UsoRecurso, nome: RecursoLimitado): string {
  if (ilimitado(recurso)) return `${recurso.uso} ${RECURSO_LABEL[nome]}`;
  return `${recurso.uso} de ${recurso.limite} ${RECURSO_LABEL[nome]}`;
}

export interface ErroDeLimite {
  recurso: RecursoLimitado;
  planoSugerido: string | null;
  mensagem: string;
}

/**
 * Traduz o erro do gatilho de limite para uma mensagem de tela.
 *
 * O gatilho devolve `hint = 'limite_plano:<recurso>'` e, em `detail`, o id do
 * plano que comporta o uso pretendido. Erro de outra natureza devolve null,
 * para que a tela mostre a mensagem original.
 */
export function interpretarErroDeLimite(erro: unknown): ErroDeLimite | null {
  if (!erro || typeof erro !== 'object') return null;

  const { hint, details, message } = erro as {
    hint?: string | null;
    details?: string | null;
    message?: string | null;
  };

  if (!hint || !hint.startsWith('limite_plano:')) return null;

  const recurso = hint.slice('limite_plano:'.length) as RecursoLimitado;
  if (!RECURSO_LABEL[recurso]) return null;

  const planoSugerido = details && details.trim() !== '' ? details.trim() : null;

  return {
    recurso,
    planoSugerido,
    mensagem: message || `Limite de ${RECURSO_LABEL[recurso]} do plano atingido.`,
  };
}

export function mensagemDeUpgrade(erro: ErroDeLimite, nomeDoPlanoSugerido?: string | null): string {
  const base = erro.mensagem;
  if (!erro.planoSugerido) return `${base}. Fale com o suporte para ampliar o plano.`;
  const nome = nomeDoPlanoSugerido || erro.planoSugerido;
  return `${base}. O plano ${nome} comporta esse uso.`;
}

// ─── Leitura com cache ───────────────────────────────────────────────
// Configuração é lida uma vez por sessão e reaproveitada (ADR-0011). Toda
// operação que muda o uso invalida o cache.

let cache: UsoDoPlano | null = null;
let emVoo: Promise<UsoDoPlano | null> | null = null;

export function invalidarUsoDoPlano(): void {
  cache = null;
  emVoo = null;
}

export async function carregarUsoDoPlano(forcar = false): Promise<UsoDoPlano | null> {
  if (!forcar && cache) return cache;
  if (!forcar && emVoo) return emVoo;

  emVoo = Promise.resolve(supabase.rpc('uso_do_plano'))
    .then(({ data, error }) => {
      if (error || !data || (data as { erro?: string }).erro) {
        return null;
      }
      cache = data as UsoDoPlano;
      return cache;
    })
    .catch(() => null)
    .finally(() => {
      emVoo = null;
    });

  return emVoo;
}
